import { useRef, useEffect } from 'react'
import { useWinners } from '@/shared/hooks'
import { fmtEur } from '@/shared/lib'

const SPEED = 45

function buildItemHTML(amount: string, name: string): string {
  return `<span class="inline-flex items-center gap-2.5 px-5 whitespace-nowrap"><span class="font-bold text-sm text-emerald-400">+${amount}</span><span class="text-gray-600">•</span><span class="text-sm font-medium text-gray-400">${name}</span></span>`
}

function buildHalfHTML(items: Array<{ a: string; n: string }>): string {
  return items.map(i => buildItemHTML(i.a, i.n)).join('')
}

export function Ticker() {
  const { data: winners } = useWinners(100)

  const containerRef = useRef<HTMLDivElement>(null)
  const animRef = useRef({ raf: 0, offset: 0, prevTime: 0, halfWidth: 0 })
  const initializedRef = useRef(false)

  // Store latest winners in a ref so the setup effect doesn't depend on it
  const winnersRef = useRef(winners)
  winnersRef.current = winners

  // One-time setup: create DOM and start animation (runs only on mount)
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let raf = 0
    let track: HTMLDivElement | null = null

    function tryInit() {
      const w = winnersRef.current
      if (!w || w.length === 0 || initializedRef.current) return

      const items = w.slice(0, 20).map(r => ({ a: fmtEur(r.amount), n: r.name }))
      const html = buildHalfHTML(items)

      track = document.createElement('div')
      track.style.cssText = 'display:flex;align-items:center;height:100%;width:max-content;will-change:transform;backface-visibility:hidden'

      const half1 = document.createElement('div')
      half1.style.cssText = 'display:flex;align-items:center;flex-shrink:0'
      half1.innerHTML = html
      const half2 = document.createElement('div')
      half2.style.cssText = 'display:flex;align-items:center;flex-shrink:0'
      half2.setAttribute('aria-hidden', 'true')
      half2.innerHTML = html

      track.appendChild(half1)
      track.appendChild(half2)
      container!.appendChild(track)

      const a = animRef.current
      a.halfWidth = half1.getBoundingClientRect().width
      a.offset = 0
      a.prevTime = 0
      initializedRef.current = true

      return track
    }

    const t = tryInit()
    if (!t) {
      // Data not ready yet — poll until it arrives
      const interval = setInterval(() => {
        const t2 = tryInit()
        if (t2) {
          track = t2
          startAnimation(t2)
          clearInterval(interval)
        }
      }, 100)
      return () => {
        clearInterval(interval)
        cancelAnimationFrame(raf)
        container!.innerHTML = ''
        initializedRef.current = false
      }
    }

    function startAnimation(el: HTMLDivElement) {
      const a = animRef.current
      function animate(now: number) {
        if (a.prevTime === 0) a.prevTime = now
        const dt = (now - a.prevTime) / 1000
        a.prevTime = now

        if (a.halfWidth > 0) {
          a.offset += SPEED * dt
          if (a.offset >= a.halfWidth) a.offset -= a.halfWidth
        }

        el.style.transform = `translate3d(-${a.offset}px,0,0)`
        raf = requestAnimationFrame(animate)
      }
      raf = requestAnimationFrame(animate)
    }

    startAnimation(t)

    return () => {
      cancelAnimationFrame(raf)
      container!.innerHTML = ''
      initializedRef.current = false
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={containerRef}
      className="relative h-10 overflow-hidden bg-surface-900 border-b border-white/[0.04]"
    />
  )
}